/**
 * The facts every document shows, in rows and in the preview header: who can see it, confidentiality, type,
 * company, size and date. Plus the list row itself.
 */
import clsx from "clsx";
import { FileText, Lock, Users } from "lucide-react";
import { useState, type ReactNode } from "react";
import { day, humanSize, thumbUrl, type FdDoc } from "./fd";

const CONF_TONE: Record<string, string> = {
  internal: "bg-slate-100 text-slate-700 ring-slate-200",
  confidential: "bg-amber-50 text-amber-800 ring-amber-200",
  restricted: "bg-red-50 text-red-700 ring-red-200",
};

export function ConfBadge({ value }: { value?: string }) {
  if (!value) return null;
  return <span className={clsx("badge capitalize ring-1", CONF_TONE[value.toLowerCase()] ?? CONF_TONE.internal)}>{value}</span>;
}

export function AccessTag({ access }: { access?: FdDoc["access"] }) {
  if (access?.mode === "owner") return <span className="badge bg-brand-50 text-brand-700 ring-1 ring-brand-200"><Lock size={11} aria-hidden /> Only you</span>;
  if (access?.mode === "listed") {
    const n = access.members?.length ?? 0;
    return <span className="badge bg-brand-50 text-brand-700 ring-1 ring-brand-200"><Users size={11} aria-hidden /> {n ? `Shared with ${n}` : "Shared with a list"}</span>;
  }
  return null;
}

/** Badges then a muted line: type · company · size · date. */
export function DocFacts({ doc, className }: { doc: FdDoc; className?: string }) {
  const bits = [doc.type, doc.company, humanSize(doc.currentVersion?.sizeBytes), day(doc.updatedAt)].filter(Boolean);
  return (
    <span className={clsx("flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1", className)}>
      <AccessTag access={doc.access} />
      <ConfBadge value={doc.confidentiality} />
      {bits.length > 0 && <span className="muted min-w-0 truncate text-xs">{bits.join(" · ")}</span>}
    </span>
  );
}

/** «match» → <mark>, as FusionDocs marks search snippets. */
export function Snip({ text }: { text: string }) {
  const parts = text.replace(/\s+/g, " ").split(/(«[^»]*»)/g);
  return <>{parts.map((p, i) => (p.startsWith("«") ? <mark key={i} className="rounded bg-amber-100 px-0.5 text-inherit">{p.slice(1, -1)}</mark> : <span key={i}>{p}</span>))}</>;
}

/** Thumbnails FusionDocs hasn't made (404) or that failed, so they aren't asked for again this visit. */
const noThumb = new Set<string>();

/** FusionDocs' small JPEG of the file, lazy-loaded at a fixed size; the file icon until it exists. */
export function Thumb({ id, active }: { id: string; active?: boolean }) {
  const [bad, setBad] = useState(() => noThumb.has(id));
  const box = "h-10 w-10 shrink-0 rounded-md border border-[var(--line)] bg-[var(--panel-2)]";
  if (bad) {
    return <span className={clsx(box, "grid place-items-center")}><FileText size={18} className={active ? "text-brand-600" : "muted"} aria-hidden /></span>;
  }
  return (
    <img src={thumbUrl(id)} alt="" loading="lazy" decoding="async" width={40} height={40} className={clsx(box, "object-cover")}
      onError={() => { noThumb.add(id); setBad(true); }} />
  );
}

export function DocRow({ doc, active, onOpen, extra, thumb }: { doc: FdDoc; active?: boolean; onOpen: (id: string) => void; extra?: ReactNode; thumb?: boolean }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(doc.id)}
        aria-current={active ? "true" : undefined}
        className={clsx(
          "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors sm:px-5",
          active ? "bg-brand-50" : "hover:bg-[var(--panel-2)]",
        )}
      >
        {thumb ? <Thumb id={doc.id} active={active} /> : <FileText size={18} className={clsx("mt-0.5 shrink-0", active ? "text-brand-600" : "muted")} aria-hidden />}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{doc.title || "Untitled"}</span>
          <DocFacts doc={doc} className="mt-1" />
          {doc.snippet && <span className="muted mt-1 line-clamp-2 block text-xs"><Snip text={doc.snippet} /></span>}
          {doc.filingPath && <span className="mt-1 block truncate text-[0.7rem] text-[var(--subtle)]">{doc.filingPath}</span>}
          {extra}
        </span>
      </button>
    </li>
  );
}
